// @ts-nocheck
import {useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useLocation, useNavigate, useSearchParams} from "react-router-dom";
import {BaseBreadcrumb, BaseButton, BaseTabs} from "@/components";
import FloatButtonWallet from "../../../components/FloatButton";
import ImprovedConditionalPaymentProfileCard from "../../../components/ImprovedConditionalPaymentProfileCard";
import ProtectedRoute from "../../../components/ProtectedRoute";
import List from "./components/List";
import DefaultLottery from "./Games/DefaultLottery";
import useGetLotteryGames from "./hooks/useGetLotteryGames";
import {transformLotteryGamesForList} from "./utils/lotteryDataTransformers";

const lotteryWrapperCls = "min-h-[90vh] h-full";
const lotteryWrapperMobileCls = "pt-4";
const tabsCls =
  "mb-4 pl-5 [&_.ant-tabs-tab]:font-[var(--font-family)] [&_.ant-tabs-tab]:font-semibold [&_.ant-tabs-tab]:text-[var(--font-body-lg)] [&_.ant-tabs-tab-active_.ant-tabs-tab-btn]:text-[var(--primary)]! max-[480px]:[&_.ant-tabs-tab]:text-sm";
const pageHeaderCls =
  "flex items-center justify-between gap-4 px-5 pt-4 pb-2";
const pageTitleCls =
  "m-0 font-semibold text-(length:--font-body-lg) text-[var(--color-text-secondary)] tracking-tight leading-tight";
const myTicketsBtnCls =
  "!h-9 !text-[13px] !font-bold !rounded-full !px-5 !border !border-[var(--color-accent-magenta)] !text-[var(--color-accent-magenta)] !bg-transparent hover:!bg-[linear-gradient(90deg,rgba(250,84,0,0.9)_0%,rgba(215,50,168,0.9)_100%)] hover:!text-white hover:!border-transparent transition-all duration-200";

const CATEGORY_MAP = {
  animalitos: "animalitos",
  triples: "triples",
  terminales: "terminales",
};

const Lottery = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {t} = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = window.innerWidth <= 768;

  // Get lottery games data
  const {data: lotteryGamesResponse, isLoading, isError} = useGetLotteryGames();

  const lotteryGames = useMemo(() => {
    if (!lotteryGamesResponse?.data?.data) {
      return [];
    }
    return transformLotteryGamesForList(lotteryGamesResponse.data.data);
  }, [lotteryGamesResponse]);

  // Get category from URL path (navbar or direct link)
  const getCategoryFromPath = () => {
    const path = location.pathname;
    const match = path.match(/\/lottery\/(\w+)/);
    if (match && CATEGORY_MAP[match[1]]) {
      return match[1];
    }
    return "todos";
  };
  const category = getCategoryFromPath();

  // Tabs state for category selection, synced with URL
  const [selectedTab, setSelectedTab] = useState(category);

  // Sync tab selection with navbar/category changes
  useEffect(() => {
    setSelectedTab(category);
  }, [category]);

  // When tab changes, update router to match tab selection
  const handleTabChange = key => {
    setSelectedTab(key);
    if (key === "todos") {
      navigate("/lottery");
    } else {
      navigate(`/lottery/${key}`);
    }
  };

  // Filter games by category using game_type
  const filteredGames = useMemo(() => {
    if (category === "todos") return lotteryGames;
    if (category === "animalitos") {
      return lotteryGames.filter(
        game => game.gameType?.toLowerCase() === "animals" || game.gameType?.toLowerCase() === "animalitos"
      );
    }
    if (category === "triples") {
      return lotteryGames.filter(
        game => game.gameType?.toLowerCase() === "triple" || game.gameType?.toLowerCase() === "triplefacil"
      );
    }
    if (category === "terminales") {
      return lotteryGames.filter(game =>
        ["numbers", "terminales", "terminal", "number"].includes(game.gameType?.toLowerCase())
      );
    }
    return [];
  }, [lotteryGames, category]);

  // State to track the selected game
  const [selectedGameId, setSelectedGameId] = useState(null);
  const hasProcessedUrlParam = useRef(false);
  const bettingAreaRef = useRef(null);

  // Reset selectedGameId when category changes
  useEffect(() => {
    setSelectedGameId(null);
    hasProcessedUrlParam.current = false;
  }, [category]);

  // Auto-select the first game when games are loaded, or select from URL param
  useEffect(() => {
    if (filteredGames.length === 0) return;
    const gameIdFromUrl = searchParams.get("gameId");
    if (gameIdFromUrl && !hasProcessedUrlParam.current) {
      const gameByUuid = filteredGames.find(game => game.id === gameIdFromUrl);
      if (gameByUuid) {
        hasProcessedUrlParam.current = true;
        setSelectedGameId(gameByUuid.stringId);
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete("gameId");
        setSearchParams(newSearchParams, {replace: true});
        return;
      }
    }
    // Do NOT auto-select the first game. Only set selectedGameId when user clicks a game card.
  }, [filteredGames, searchParams]);

  const handleMisTicketsClick = () => {
    navigate("/lottery/tickets");
  };

  const handleGameSelect = gameId => {
    setSelectedGameId(gameId);
    // Auto-scroll to betting area after a short delay to allow rendering
    setTimeout(() => {
      if (bettingAreaRef.current) {
        bettingAreaRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 100);
  };

  // Filter games by tab selection (does not affect existing category logic)
  const tabFilteredGames = useMemo(() => {
    if (selectedTab === "todos") return lotteryGames;
    if (selectedTab === "animalitos") {
      return lotteryGames.filter(
        game => game.gameType?.toLowerCase() === "animals" || game.gameType?.toLowerCase() === "animalitos"
      );
    }
    if (selectedTab === "triples") {
      return lotteryGames.filter(
        game => game.gameType?.toLowerCase() === "triple" || game.gameType?.toLowerCase() === "triplefacil"
      );
    }
    if (selectedTab === "terminales") {
      return lotteryGames.filter(game =>
        ["numbers", "terminales", "terminal", "number"].includes(game.gameType?.toLowerCase())
      );
    }
    return [];
  }, [lotteryGames, selectedTab]);

  return (
    <ProtectedRoute>
      <ImprovedConditionalPaymentProfileCard />
      <div className="px-4 pt-3">
        <BaseBreadcrumb items={[{label: "Lotería"}]} />
      </div>
      <div className={`games-list ${lotteryWrapperCls} ${isMobile ? lotteryWrapperMobileCls : ""}`}>
        <div className={pageHeaderCls}>
          <p className={pageTitleCls}>Consulta tus tickets</p>
          <BaseButton
            variant="secondary"
            onClick={handleMisTicketsClick}
            className={myTicketsBtnCls}
          >
            {t("myLotteryTicket")}
          </BaseButton>
        </div>
        {/* Tabs for lottery categories, synced with navbar and router */}
        <BaseTabs
          activeKey={selectedTab}
          onChange={handleTabChange}
          tabBarClassName={tabsCls}
          items={[
            {key: "todos", label: t("All", "Todos")},
            {key: "animalitos", label: t("Animalitos")},
            {key: "triples", label: t("Triples")},
            {key: "terminales", label: t("Terminales")},
          ]}
        />
        {/* Render the games list with selected game highlighting */}
        <List
          selectedGameId={selectedGameId}
          onGameSelect={handleGameSelect}
          games={tabFilteredGames}
          loading={isLoading}
          error={isError}
        />
        {/* Only render the selected game details if a valid game is selected */}
        {typeof selectedGameId === "string" && selectedGameId && (
          <div ref={bettingAreaRef}>
            <DefaultLottery gameIdString={selectedGameId} />
          </div>
        )}
        <FloatButtonWallet />
      </div>
    </ProtectedRoute>
  );
};

export default Lottery;
